/**
 * The extension ids `/login` will hand a session token to.
 *
 * ── Why an allowlist ────────────────────────────────────────────────────────
 *
 * The handshake (`extension-auth.ts`) works by the extension opening
 * `/login?source=extension&extId=<its own id>` and the page messaging that id
 * back with a **14-day session token** once the user signs in. Without a list to
 * check against, the page sends the token to whatever `extId` the URL happened
 * to carry — so any extension, or any page that can get a user to follow a link,
 * could name itself and be handed a session.
 *
 * It is narrower than it first sounds: `useExtensionAuthHandoff` fires only
 * *after* a sign-in completes on that visit, deliberately not on mount, so
 * somebody already signed in who merely loads the URL is not exposed. But it is
 * still phishing-shaped — the user sees Floto's own real login page and a
 * successful sign-in — and it costs one comparison to close.
 *
 * ── Keeping it right ────────────────────────────────────────────────────────
 *
 * An id here must match the `key` pinned in that extension's manifest, which is
 * what stops Chrome assigning a fresh id per unpacked load. Adding an extension
 * means adding its id here **and** to `externally_connectable.matches` on its
 * side; a Chrome Web Store build has a different id from an unpacked one, so a
 * published extension needs its store id added too.
 *
 * A missing id fails closed and visibly — the extension's sign-in tab opens, the
 * user signs in, and nothing arrives — which is the right direction for this to
 * fail in.
 *
 * Ships as TypeScript source, like the rest of this directory: it has a runtime
 * value and its only consumers are bundlers, not Node.
 */
export const TRUSTED_EXTENSION_IDS: readonly string[] = [
  /** `apps/issue-report-extension` — Floto Report. */
  'gcnjffjgicedmcjceoejflbklgnepgeg',
  /**
   * `apps/agent-extension` pins no `key`, so it has no id to list here: Chrome
   * derives an unpacked one from the load path, which differs per machine. Add
   * yours (from `chrome://extensions`) to sign in from a local build, and add
   * the Web Store id when it is published. Until then its handoff fails
   * closed — the sign-in tab opens, the user signs in, nothing arrives.
   */
];

/**
 * Whether this id may be handed a session.
 *
 * `allowed` is the **effective** list for the deployment the caller is talking
 * to — `GET /auth/extension-ids`, which is the compiled list above plus, on a
 * test deployment only, whatever an admin added in `platformSettings`. It
 * exists because Chrome derives an unpacked extension's id from the path it was
 * loaded from, so no developer's local build has an id worth committing.
 *
 * It defaults to the constant, and every caller must fall back to that default
 * when it cannot reach the server: **failing closed here means failing to the
 * shipped list, never to an empty one.** An empty list would break the
 * published extension for everybody the moment a fetch times out, which is a
 * worse failure than not honouring somebody's local id.
 *
 * Pure, and takes the list rather than fetching it, so the decision is testable
 * without a network and the same function serves both sign-in pages.
 */
export function isTrustedExtensionId(
  id: string | null,
  allowed: readonly string[] = TRUSTED_EXTENSION_IDS,
): id is string {
  return id !== null && allowed.includes(id);
}
